import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Pages
import EnhancedLandingPage from './pages/EnhancedLandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import BookDemoPage from './pages/BookDemoPage';
import CareersPage from './pages/CareersPage';
import UtrederLandingPage from './pages/UtrederLandingPage';
import PartnerUtrederPage from './pages/PartnerUtrederPage';
import PricingInvestigationPage from './pages/PricingInvestigationPage';

// Resource Pages
import LovverkPage from './pages/resources/LovverkPage';
import EksternVarslingPage from './pages/resources/EksternVarslingPage';
import CompliancePage from './pages/resources/CompliancePage';
import HmsVarslingPage from './pages/resources/HmsVarslingPage';
import AnonymVarslingPage from './pages/resources/AnonymVarslingPage';
import HowItWorksPage from './pages/resources/HowItWorksPage';
import SecurityAuditPage from './pages/SecurityAuditPage';
import SupportPage from './modules/company/pages/SupportPage';

// Modules
import WhistleblowerModule from './modules/whistleblower/WhistleblowerModule';
import CompanyModule from './modules/company/CompanyModule';
import ReportedPersonModule from './modules/reported/ReportedPersonModule';
import HMSModule from './modules/hms/HMSModule';
import SuperAdminModule from './modules/superadmin/SuperAdminModule';
import PartnerModule from './modules/partner/PartnerModule';
import SellerModule from './modules/seller/SellerModule';
import UtrederModule from './modules/utreder/UtrederModule';

// Public Portal Pages
import CompanyWelcome from './modules/whistleblower/pages/CompanyWelcome';
import WhistleblowerForm from './modules/whistleblower/pages/WhistleblowerForm';
import PublicRUHForm from './modules/whistleblower/pages/PublicRUHForm';
import WhistleblowerConfirmation from './modules/whistleblower/pages/WhistleblowerConfirmation';
import UtrederPublicProfile from './modules/utreder/pages/UtrederPublicProfile';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { InvestigationProvider } from './contexts/InvestigationContext';
import { CookieProvider } from './contexts/CookieContext';
import { EmailProvider } from './contexts/EmailContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  const [appMounted, setAppMounted] = useState(false);

  useEffect(() => {
    setAppMounted(true);
    
    // LEGACY REDIRECT: Fanger opp gamle lenker med #/s/ og sender dem til /s/
    if (window.location.hash.includes('#/s/')) {
      const companyId = window.location.hash.split('#/s/')[1];
      if (companyId) {
        window.location.href = window.location.origin + '/s/' + companyId;
      }
    }
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <ThemeProvider>
          <AuthProvider>
            <DataProvider>
              <InvestigationProvider>
                <CookieProvider>
                  <EmailProvider>
                    <div className="App dark:bg-gray-900 min-h-screen transition-colors duration-200">
                      {!appMounted && (
                        <div className="fixed inset-0 flex items-center justify-center bg-white z-[9999]">
                          <p className="text-gray-500">Initialiserer applikasjon...</p>
                        </div>
                      )}
                      
                      <Routes>
                        {/* --- PUBLIC ROUTES (CLEAN URLS) --- */}
                        <Route path="/s/:companyId" element={<CompanyWelcome />} />
                        <Route path="/portal/form/:companyId" element={<WhistleblowerForm />} />
                        <Route path="/portal/ruh/:companyId" element={<PublicRUHForm />} />
                        <Route path="/portal/confirmation" element={<WhistleblowerConfirmation />} />
                        <Route path="/u/:slug" element={<UtrederPublicProfile />} />
                        
                        <Route path="/" element={<EnhancedLandingPage />} />
                        <Route path="/book-demo" element={<BookDemoPage />} />
                        <Route path="/karriere" element={<CareersPage />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/cookies" element={<CookiePolicy />} />
                        <Route path="/arbeidsmiljoloven-varsling" element={<LovverkPage />} />
                        <Route path="/ekstern-varsling" element={<EksternVarslingPage />} />
                        <Route path="/regelverk-og-etterlevelse" element={<CompliancePage />} />
                        <Route path="/hms-varsling" element={<HmsVarslingPage />} />
                        <Route path="/anonym-varsling" element={<AnonymVarslingPage />} />
                        <Route path="/hvordan-fungerer-det" element={<HowItWorksPage />} />
                        <Route path="/sikkerhet" element={<SecurityAuditPage />} />
                        <Route path="/support" element={<SupportPage />} />
                        <Route path="/priser-utredning" element={<PricingInvestigationPage />} />

                        {/* --- Modules --- */}
                        <Route path="/whistleblower/*" element={<WhistleblowerModule />} />
                        <Route path="/company/*" element={<CompanyModule />} />
                        <Route path="/reported/*" element={<ReportedPersonModule />} />
                        <Route path="/hms/*" element={<HMSModule />} />
                        <Route path="/superadmin/*" element={<SuperAdminModule />} />
                        <Route path="/partner/*" element={<PartnerModule />} />
                        <Route path="/seller/*" element={<SellerModule />} />
                        <Route path="/utreder/*" element={<UtrederModule />} />

                        {/* Fallback */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                      
                      <CookieConsent />
                    </div>
                  </EmailProvider>
                </CookieProvider>
              </InvestigationProvider>
            </DataProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;